import { CloudArrowUpIcon, XCircleIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { createFormHook, createFormHookContexts, useStore } from '@tanstack/react-form'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { type ApiError } from '../../utils/api-client/api-error.ts'
import { usePostCodingGuideline } from '../../utils/api-client/api-hooks.ts'
import { type CodingGuidelinesResponse } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import * as footerStyles from '../drawers/floating-footer.css.ts'
import { FloatingFooter } from '../drawers/floating-footer.tsx'
import * as inputStyles from '../input/input-container.css.ts'
import { InputContainer } from '../input/input-container.tsx'
import { TextInput } from '../input/text-input.tsx'
import * as styles from './coding-guidelines-drawer.css.ts'
import {
  formatFileSize,
  getDocType,
  getDocTypeBadgeClass,
  UPLOAD_ALLOWED_EXTS,
  UPLOAD_MAX_BYTES,
} from './coding-guidelines-types.ts'
import { DocTypeIcon } from './doc-type-icon.tsx'

// #region Types

export type UploadFormValues = {
  displayName: string
  file: File | undefined
}

export type UploadFormProps = {
  organizationId?: string
  onSuccess: (document: CodingGuidelinesResponse, file: File) => unknown
  onCancel: () => unknown
  onUploadStart?: (displayName: string, file: File) => unknown
  onUploadProgress?: (progressPercent: number) => unknown
  onUploadError?: (error: ApiError | Error) => unknown
}

// #endregion

// #region Form Hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: UploadFormValues = {
  displayName: '',
  file: undefined,
}

export const useUploadForm = (defaultValueOverrides: Partial<UploadFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

export const UploadForm: React.FC<UploadFormProps> = ({
  organizationId,
  onSuccess,
  onCancel,
  onUploadStart,
  onUploadProgress,
  onUploadError,
}) => {
  const form = useFormContext()
  const values = useStore(form.store, state => state.values)
  const drawerContentRef = useRef<HTMLDivElement | null>(null)

  const showError = useShowMutationError({ prefix: 'Failed to upload document' })
  const uploadMutation = usePostCodingGuideline({ organizationId, onError: showError })

  const isSubmitDisabled = !values.displayName.trim() || values.file === undefined || uploadMutation.isPending

  const handleSubmit = () => {
    if (!values.file) {
      return
    }
    const submittedFile = values.file
    const resolvedDisplayName = values.displayName.trim() || submittedFile.name
    onUploadStart?.(resolvedDisplayName, submittedFile)
    uploadMutation.mutate(
      { displayName: values.displayName.trim(), file: submittedFile, onUploadProgress },
      {
        onSuccess: data => {
          onSuccess(data, submittedFile)
        },
        onError: error => {
          onUploadError?.(error)
        },
      }
    )
  }

  // The floating footer must stay the immediate sibling of Dialog.Content (its slide-in
  // animation is driven by the `div[data-has-floating-footer] + &` selector), so the form
  // renders both via a fragment rather than letting the host place the footer separately.
  return (
    <>
      <Dialog.Content
        ref={drawerContentRef}
        aria-label="Add new document"
        className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
        data-has-floating-footer={true}
        onInteractOutside={event => event.preventDefault()}
      >
        <Dialog.Title className={styles.title}>Add new document</Dialog.Title>
        <p className={styles.drawerSubhead}>
          Pixee stores this as additional context when analyzing findings — the more specific, the better.
        </p>
        <form className={styles.form} onSubmit={e => e.preventDefault()}>
          <DisplayNameField />
          <InputContainer label="Document">
            <FileDropzoneField />
          </InputContainer>
        </form>
      </Dialog.Content>
      <FloatingFooter drawerContentRef={drawerContentRef}>
        <span />
        <div className={styles.footerRight}>
          <GhostButton onClick={onCancel} state={uploadMutation.isPending ? 'disabled' : 'default'}>
            Cancel
          </GhostButton>
          <DefaultButton
            state={uploadMutation.isPending ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
            onClick={handleSubmit}
            loadingLabel="Uploading…"
          >
            Upload
          </DefaultButton>
        </div>
      </FloatingFooter>
    </>
  )
}

const DisplayNameField: React.FC = () => {
  const form = useFormContext()
  const displayName = useStore(form.store, state => state.values.displayName)
  const [isTouched, setIsTouched] = useState(false)
  const hasError = isTouched && !displayName.trim()
  return (
    <TextInput
      id="upload-display-name"
      name="displayName"
      label="Name"
      isRequired
      value={displayName}
      isError={hasError}
      errorText={hasError ? 'A document name is required' : undefined}
      placeholder="e.g. Acme Corp. Secure Coding Standards v3"
      onChange={value => form.setFieldValue('displayName', value)}
      onBlur={() => setIsTouched(true)}
    />
  )
}

const FileDropzoneField: React.FC = () => {
  const form = useFormContext()
  const file = useStore(form.store, state => state.values.file)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  // Kept local (not lifted to UploadForm) to avoid re-renders propagating into Dialog.Content → FocusScope → React 19 setState loop.
  const [fileError, setFileError] = useState<string | undefined>()

  const acceptFile = (incoming: File | undefined) => {
    if (!incoming) {
      return
    }
    if (!getDocType(incoming.name)) {
      const ext = incoming.name.includes('.') ? incoming.name.split('.').pop()?.toLowerCase() : undefined
      const identifier = ext ? `.${ext}` : incoming.name
      setFileError(
        `"${identifier}" is not a supported file type. Convert the document to a supported format (.pdf, .md, .docx, .txt, .html up to 5 MB) and try again.`
      )
      return
    }
    if (incoming.size > UPLOAD_MAX_BYTES) {
      setFileError(
        'The selected file is larger than the allowed 5 MB. Trim the document or separate it into multiple files and try again.'
      )
      return
    }
    setFileError(undefined)
    form.setFieldValue('file', incoming)
    form.setFieldValue('displayName', incoming.name.replace(/\.[^.]+$/, ''))
  }

  const clearFile = () => {
    form.setFieldValue('file', undefined)
    setFileError(undefined)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (file) {
    const docType = getDocType(file.name)
    const iconBadgeStyle = getDocTypeBadgeClass({
      docType,
      classes: {
        base: styles.currentDocIconBadge,
        pdf: styles.currentDocIconBadgePdf,
        docx: styles.currentDocIconBadgeDocx,
        md: styles.currentDocIconBadgeMd,
        txt: styles.currentDocIconBadgeTxt,
        html: styles.currentDocIconBadgeHtml,
      },
    })
    return (
      <div className={styles.selectedFile}>
        <span className={iconBadgeStyle} aria-hidden>
          <DocTypeIcon docType={docType} size={18} />
        </span>
        <div className={styles.selectedFileMeta}>
          <p className={styles.selectedFileName}>{file.name}</p>
          <p className={styles.selectedFileSize}>{formatFileSize(file.size)}</p>
        </div>
        <button type="button" className={styles.selectedFileClear} aria-label="Remove file" onClick={clearFile}>
          <XIcon size={18} weight="bold" />
        </button>
      </div>
    )
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept={UPLOAD_ALLOWED_EXTS.map(e => `.${e}`).join(',')}
        onChange={e => acceptFile(e.target.files?.[0])}
      />
      <div
        role="button"
        tabIndex={0}
        className={classNames(
          styles.dropzone,
          isDragOver && styles.dropzoneActive,
          fileError && styles.dropzoneErrorBorder
        )}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            fileInputRef.current?.click()
          }
        }}
        onDragOver={e => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={e => {
          e.preventDefault()
          setIsDragOver(false)
          acceptFile(e.dataTransfer.files[0])
        }}
      >
        <CloudArrowUpIcon size={32} weight="light" />
        <p className={styles.dropzoneLabel}>
          Drop a file here, or{' '}
          <button
            type="button"
            className={styles.dropzoneBrowseLink}
            onClick={e => {
              e.stopPropagation()
              fileInputRef.current?.click()
            }}
          >
            browse
          </button>
        </p>
        <p className={styles.dropzoneSub}>Acceptable: .pdf, .md, .docx, .txt, .html up to 5 MB</p>
      </div>
      {fileError ? (
        <span className={inputStyles.errorText}>
          <XCircleIcon size={14} weight="light" />
          {fileError}
        </span>
      ) : null}
    </>
  )
}
